import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { PwaBridge } from "@/components/PwaBridge";
import { isStandalone, lastSpace } from "@/lib/pwa";
import { trackVisit } from "@/lib/tracking";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { EstablishmentProvider } from "@/context/EstablishmentContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Cgv from "@/pages/Cgv";
import Kiosk from "@/pages/Kiosk";
import KioskDevice, { KioskActivate, KioskGate } from "@/pages/KioskDevice";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import PillarDetail from "@/pages/PillarDetail";
import SiteAudit from "@/pages/SiteAudit";
import Advisor from "@/pages/Advisor";
import Generators from "@/pages/Generators";
import ActionPlan from "@/pages/ActionPlan";
import Reputation from "@/pages/Reputation";
import GoogleBusiness from "@/pages/GoogleBusiness";
import Loyalty from "@/pages/Loyalty";
import Agenda from "@/pages/Agenda";
import Staffing from "@/pages/Staffing";
import Notifications from "@/pages/Notifications";
import InsightSheet from "@/pages/InsightSheet";
import NotificationPrefs from "@/pages/NotificationPrefs";
import Distribution from "@/pages/Distribution";
import SpaceChooser from "@/pages/SpaceChooser";
import EmployeePortal, { EmployeeLogin, EmployeeActivate } from "@/pages/EmployeePortal";
import EmployeeLoginLink from "@/pages/EmployeeLoginLink";
import { LoyaltySignup, LoyaltyCard, LoyaltyUnsubscribe } from "@/pages/LoyaltyPublic";
import PlayGame from "@/pages/PlayGame";
import RewardView from "@/pages/RewardView";
import VoucherView from "@/pages/VoucherView";
import VoucherCustomize from "@/pages/VoucherCustomize";
import AIVisibility from "@/pages/AIVisibility";
import SocialAssistant from "@/pages/SocialAssistant";
import Competitors from "@/pages/Competitors";
import Establishments from "@/pages/Establishments";
import EstablishmentForm from "@/pages/EstablishmentForm";
import Settings from "@/pages/Settings";
import BillingStart, { BillingSuccess, BillingCancel } from "@/pages/Billing";
import ChoosePlan from "@/pages/ChoosePlan";
import BillingPending from "@/pages/BillingPending";
import Admin from "@/pages/Admin";
import ActivityInsights from "@/pages/ActivityInsights";
import Operations, { OperationsRedirect } from "@/pages/Operations";
import LegacyRedirect from "@/components/LegacyRedirect";
import Onboarding from "@/pages/Onboarding";
import Purchases from "@/pages/Purchases";
import Messages from "@/pages/Messages";

/** Lancement depuis l'icône (PWA) : renvoie vers le dernier espace utilisé (employeur ou salarié) au lieu de la page vitrine. */
function StandaloneStart({ children }) {
  const sp = new URLSearchParams(window.location.search);
  const pwa = (isStandalone() || sp.get("source") === "pwa") && !sp.get("site");
  if (pwa && sp.get("espace")) return <Navigate replace to={sp.get("espace") === "salarie" ? "/salarie" : "/app/dashboard"} />;
  if (pwa && lastSpace()) return <Navigate replace to={lastSpace() === "employee" ? "/salarie" : "/app/dashboard"} />;
  if (pwa) return <Navigate replace to="/espaces" />;
  return children;
}

function App() {
  useEffect(() => { trackVisit(); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <EstablishmentProvider>
          <PwaBridge />
          <Routes>
            <Route path="/" element={<KioskGate><StandaloneStart><Landing /></StandaloneStart></KioskGate>} />
            <Route path="/login" element={<Login />} />
            <Route path="/espaces" element={<SpaceChooser />} />
            <Route path="/cgv" element={<Cgv />} />
            <Route path="/pointeuse/:token" element={<Kiosk />} />
            <Route path="/borne" element={<KioskDevice />} />
            <Route path="/borne/activer/:code" element={<KioskActivate />} />
            <Route path="/register" element={<Register />} />
            <Route path="/choisir" element={<ChoosePlan />} />
            <Route path="/choisir/:plan" element={<ChoosePlan />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/fidelite/:code" element={<LoyaltySignup />} />
            <Route path="/carte/:token" element={<LoyaltyCard />} />
            <Route path="/jouer/:token" element={<PlayGame />} />
            <Route path="/gain/:token" element={<RewardView />} />
            <Route path="/bon/:token" element={<VoucherView />} />
            <Route path="/personnaliser/:ctoken" element={<VoucherCustomize />} />
            <Route path="/carte/:token/desinscription" element={<LoyaltyUnsubscribe />} />
            <Route path="/salarie" element={<EmployeePortal />} />
            <Route path="/salarie/connexion" element={<EmployeeLogin />} />
            <Route path="/salarie/activer/:token" element={<EmployeeActivate />} />
            <Route path="/salarie/connexion/lien/:token" element={<EmployeeLoginLink />} />
            <Route path="/salarie/reinitialiser/:token" element={<EmployeeActivate reset />} />
            <Route path="/app/billing/pending" element={<ProtectedRoute><BillingPending /></ProtectedRoute>} />
            <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="billing/start" element={<BillingStart />} />
              <Route path="billing/success" element={<BillingSuccess />} />
              <Route path="billing/cancel" element={<BillingCancel />} />
              <Route path="pillars/:key" element={<PillarDetail />} />
              <Route path="site-audit" element={<SiteAudit />} />
              <Route path="advisor" element={<Advisor />} />
              <Route path="generators" element={<Navigate to="/app/site-audit?tab=generators" replace />} />
              <Route path="plan" element={<ActionPlan />} />
              <Route path="activity" element={<ActivityInsights />} />
              <Route path="operations" element={<OperationsRedirect />} />
              <Route path="commandes" element={<Operations mode="orders" />} />
              <Route path="messages" element={<Messages />} />
              <Route path="controles" element={<Operations mode="controls" />} />
              <Route path="rh" element={<Staffing />} />
              <Route path="integration" element={<LegacyRedirect to="/app/rh?tab=integration" />} />
              <Route path="purchases" element={<Purchases />} />
              <Route path="reputation" element={<Reputation />} />
              <Route path="google" element={<GoogleBusiness />} />
              <Route path="fidelite" element={<Loyalty />} />
              <Route path="agenda" element={<Agenda />} />
              <Route path="planning" element={<LegacyRedirect to="/app/rh" keepQuery />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="insight/:kind/:id" element={<InsightSheet />} />
              <Route path="notifications/preferences" element={<NotificationPrefs />} />
              <Route path="notifications/diffusion" element={<Distribution />} />
              <Route path="ai-visibility" element={<Navigate to="/app/site-audit?tab=ai" replace />} />
              <Route path="social" element={<SocialAssistant />} />
              <Route path="competitors" element={<Competitors />} />
              <Route path="establishments" element={<Establishments />} />
              <Route path="establishments/new" element={<EstablishmentForm />} />
              <Route path="establishments/:id/edit" element={<EstablishmentForm />} />
              <Route path="settings" element={<Settings />} />
              <Route path="admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
        </EstablishmentProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
